import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import Loader from "@components/Loader";
import {
  useWebsiteProductsQuery,
  useProductContentQuery,
  useSaveProductDetailMutation,
  useCreateFeatureMutation,
  useSaveFeatureMutation,
  useDeleteFeatureMutation,
  useSaveDosageMutation,
  useCreateReviewMutation,
  useSaveReviewMutation,
  useDeleteReviewMutation,
  useCreateQaMutation,
  useSaveQaMutation,
  useDeleteQaMutation
} from "@queries/website";
import "@styles/opencart-admin.css";

const SOIL_LEVELS = ["light", "medium", "heavy"];
const LOAD_SIZES = ["small", "large"];

const emptyDetail = {
  rating: "",
  rating_count: "",
  verified_count: "",
  tags: "",
  tags_ar: "",
  how_to_use_intro: "",
  how_to_use_intro_ar: "",
  manual_min_ml: "",
  manual_max_ml: "",
  washes_note: "",
  washes_note_ar: "",
  concentration_note: "",
  concentration_note_ar: "",
  ingredients: "",
  ingredients_ar: "",
  tagline: "",
  tagline_ar: "",
  long_description: "",
  long_description_ar: ""
};

const emptyFeature = { title: "", title_ar: "", subtitle: "", subtitle_ar: "", image: "", sort_order: 0 };
const emptyReview = { author: "", rating: 5, body: "", source: "", verified: false };
const emptyQa = { question: "", question_ar: "", answer: "", answer_ar: "", sort_order: 0 };

function num(v) {
  return v === "" || v == null ? null : Number(v);
}

export default function ProductContentPage() {
  // Preselect a product when deep-linked with ?product_id=<id> (e.g. from the
  // website CMS "edit in admin" guide). Falls back to the first product otherwise.
  const [productId, setProductId] = React.useState(() => {
    const p = new URLSearchParams(window.location.search).get("product_id");
    return p ? Number(p) : null;
  });

  const queryClient = useQueryClient();
  const { data: products, isLoading: productsLoading } = useWebsiteProductsQuery();
  const { data: content, isFetching } = useProductContentQuery(productId);

  const saveDetail = useSaveProductDetailMutation();
  const createFeature = useCreateFeatureMutation();
  const saveFeature = useSaveFeatureMutation();
  const deleteFeature = useDeleteFeatureMutation();
  const saveDosage = useSaveDosageMutation();
  const createReview = useCreateReviewMutation();
  const saveReview = useSaveReviewMutation();
  const deleteReview = useDeleteReviewMutation();
  const createQa = useCreateQaMutation();
  const saveQa = useSaveQaMutation();
  const deleteQa = useDeleteQaMutation();

  const [detailForm, setDetailForm] = React.useState(emptyDetail);
  const [dosageGrid, setDosageGrid] = React.useState({});
  const [featureForm, setFeatureForm] = React.useState(emptyFeature);
  const [featureEditId, setFeatureEditId] = React.useState(null);
  const [reviewForm, setReviewForm] = React.useState(emptyReview);
  const [reviewEditId, setReviewEditId] = React.useState(null);
  const [qaForm, setQaForm] = React.useState(emptyQa);
  const [qaEditId, setQaEditId] = React.useState(null);

  React.useEffect(() => {
    if (!productId && products?.length) {
      setProductId(products[0].product_id);
    }
  }, [products, productId]);

  // load fetched content into local editors
  React.useEffect(() => {
    const d = content?.detail;
    setDetailForm(
      d
        ? {
            rating: d.rating ?? "",
            rating_count: d.rating_count ?? "",
            verified_count: d.verified_count ?? "",
            tags: d.tags || "",
            tags_ar: d.tags_ar || "",
            how_to_use_intro: d.how_to_use_intro || "",
            how_to_use_intro_ar: d.how_to_use_intro_ar || "",
            manual_min_ml: d.manual_min_ml ?? "",
            manual_max_ml: d.manual_max_ml ?? "",
            washes_note: d.washes_note || "",
            washes_note_ar: d.washes_note_ar || "",
            concentration_note: d.concentration_note || "",
            concentration_note_ar: d.concentration_note_ar || "",
            ingredients: d.ingredients || "",
            ingredients_ar: d.ingredients_ar || "",
            tagline: d.tagline || "",
            tagline_ar: d.tagline_ar || "",
            long_description: d.long_description || "",
            long_description_ar: d.long_description_ar || ""
          }
        : emptyDetail
    );

    const grid = {};
    (content?.dosage || []).forEach((row) => {
      grid[`${row.soil_level}|${row.load_size}`] = row.amount_ml;
    });
    setDosageGrid(grid);

    setFeatureEditId(null);
    setFeatureForm(emptyFeature);
    setReviewEditId(null);
    setReviewForm(emptyReview);
    setQaEditId(null);
    setQaForm(emptyQa);
  }, [content]);

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ["website", "product-content", productId] });
    queryClient.invalidateQueries({ queryKey: ["website", "products"] });
  }

  const onErr = (err, what) =>
    alert(
      err?.response?.data?.errors
        ? Object.values(err.response.data.errors).flat().join("\n")
        : err?.response?.data?.message || `Failed to save ${what}`
    );

  async function handleSaveDetail(e) {
    e.preventDefault();
    try {
      await saveDetail.mutateAsync({
        productId,
        payload: {
          rating: num(detailForm.rating),
          rating_count: num(detailForm.rating_count),
          verified_count: num(detailForm.verified_count),
          tags: detailForm.tags || null,
          tags_ar: detailForm.tags_ar || null,
          how_to_use_intro: detailForm.how_to_use_intro || null,
          how_to_use_intro_ar: detailForm.how_to_use_intro_ar || null,
          manual_min_ml: num(detailForm.manual_min_ml),
          manual_max_ml: num(detailForm.manual_max_ml),
          washes_note: detailForm.washes_note || null,
          washes_note_ar: detailForm.washes_note_ar || null,
          concentration_note: detailForm.concentration_note || null,
          concentration_note_ar: detailForm.concentration_note_ar || null,
          ingredients: detailForm.ingredients || null,
          ingredients_ar: detailForm.ingredients_ar || null,
          tagline: detailForm.tagline || null,
          tagline_ar: detailForm.tagline_ar || null,
          long_description: detailForm.long_description || null,
          long_description_ar: detailForm.long_description_ar || null
        }
      });
      invalidate();
    } catch (err) {
      onErr(err, "detail");
    }
  }

  async function handleSaveDosage() {
    const rows = [];
    SOIL_LEVELS.forEach((soil) =>
      LOAD_SIZES.forEach((load) => {
        const v = dosageGrid[`${soil}|${load}`];
        if (v !== "" && v != null) {
          rows.push({ soil_level: soil, load_size: load, amount_ml: Number(v) });
        }
      })
    );
    try {
      await saveDosage.mutateAsync({ productId, rows });
      invalidate();
    } catch (err) {
      onErr(err, "dosage");
    }
  }

  async function handleSaveFeature(e) {
    e.preventDefault();
    const payload = {
      title: featureForm.title,
      title_ar: featureForm.title_ar || null,
      subtitle: featureForm.subtitle || null,
      subtitle_ar: featureForm.subtitle_ar || null,
      image: featureForm.image || null,
      sort_order: Number(featureForm.sort_order) || 0
    };
    try {
      if (featureEditId) {
        await saveFeature.mutateAsync({ id: featureEditId, payload });
      } else {
        await createFeature.mutateAsync({ productId, payload });
      }
      setFeatureEditId(null);
      setFeatureForm(emptyFeature);
      invalidate();
    } catch (err) {
      onErr(err, "feature");
    }
  }

  async function handleSaveReview(e) {
    e.preventDefault();
    const payload = {
      author: reviewForm.author,
      rating: Number(reviewForm.rating) || 5,
      body: reviewForm.body || null,
      source: reviewForm.source || null,
      verified: reviewForm.verified ? 1 : 0
    };
    try {
      if (reviewEditId) {
        await saveReview.mutateAsync({ id: reviewEditId, payload });
      } else {
        await createReview.mutateAsync({ productId, payload });
      }
      setReviewEditId(null);
      setReviewForm(emptyReview);
      invalidate();
    } catch (err) {
      onErr(err, "review");
    }
  }

  async function handleSaveQa(e) {
    e.preventDefault();
    const payload = {
      question: qaForm.question,
      question_ar: qaForm.question_ar || null,
      answer: qaForm.answer,
      answer_ar: qaForm.answer_ar || null,
      sort_order: Number(qaForm.sort_order) || 0
    };
    try {
      if (qaEditId) {
        await saveQa.mutateAsync({ id: qaEditId, payload });
      } else {
        await createQa.mutateAsync({ productId, payload });
      }
      setQaEditId(null);
      setQaForm(emptyQa);
      invalidate();
    } catch (err) {
      onErr(err, "Q&A");
    }
  }

  async function del(mutation, id, what) {
    if (!window.confirm(`Delete this ${what}?`)) return;
    try {
      await mutation.mutateAsync(id);
      invalidate();
    } catch (err) {
      onErr(err, what);
    }
  }

  const product = content?.product;

  return (
    <div className="oc-content">
      {(productsLoading || isFetching) && <Loader />}

      <div className="oc-header">
        <h1>Shop Product Content</h1>
        <nav className="oc-bc">
          <span>Home</span>
          <span className="sep">›</span>
          <span>Website</span>
          <span className="sep">›</span>
          <span>Product Content</span>
        </nav>
      </div>

      <div className="oc-card">
        <div className="form-group" style={{ maxWidth: 420 }}>
          <label>Product</label>
          <select
            className="oc-input"
            value={productId || ""}
            onChange={(e) => setProductId(Number(e.target.value))}
          >
            {(products || []).map((p) => (
              <option key={p.product_id} value={p.product_id}>
                {p.name || `Product #${p.product_id}`}
              </option>
            ))}
          </select>
        </div>
        {product && (
          <div style={{ fontSize: 12, color: "#777" }}>
            #{product.product_id} · model {product.model} · base price{" "}
            {Number(product.price).toFixed(2)}. These fields power the website product page only —
            the core OpenCart product record is never written.
          </div>
        )}
      </div>

      {productId && (
        <>
          {/* ---------------- Detail ---------------- */}
          <div className="oc-card" style={{ marginTop: 16 }}>
            <div className="oc-toolbar" style={{ marginBottom: 12 }}>
              <strong>Page Detail</strong>
            </div>
            <form onSubmit={handleSaveDetail}>
              <div className="form-row">
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Description (English)</label>
                  <textarea
                    className="oc-input"
                    rows={4}
                    value={detailForm.long_description}
                    onChange={(e) =>
                      setDetailForm((f) => ({ ...f, long_description: e.target.value }))
                    }
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Description (Arabic)</label>
                  <textarea
                    className="oc-input"
                    dir="rtl"
                    rows={4}
                    value={detailForm.long_description_ar}
                    onChange={(e) =>
                      setDetailForm((f) => ({ ...f, long_description_ar: e.target.value }))
                    }
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Tagline / Short Description (English)</label>
                  <input
                    className="oc-input"
                    value={detailForm.tagline}
                    onChange={(e) => setDetailForm((f) => ({ ...f, tagline: e.target.value }))}
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Tagline / Short Description (Arabic)</label>
                  <input
                    className="oc-input"
                    dir="rtl"
                    value={detailForm.tagline_ar}
                    onChange={(e) => setDetailForm((f) => ({ ...f, tagline_ar: e.target.value }))}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Rating (0–5)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="5"
                    className="oc-input"
                    value={detailForm.rating}
                    onChange={(e) => setDetailForm((f) => ({ ...f, rating: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>Rating Count</label>
                  <input
                    type="number"
                    className="oc-input"
                    value={detailForm.rating_count}
                    onChange={(e) =>
                      setDetailForm((f) => ({ ...f, rating_count: e.target.value }))
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Verified Count</label>
                  <input
                    type="number"
                    className="oc-input"
                    value={detailForm.verified_count}
                    onChange={(e) =>
                      setDetailForm((f) => ({ ...f, verified_count: e.target.value }))
                    }
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Tags — English (comma separated chips)</label>
                  <input
                    className="oc-input"
                    placeholder="Dermatologically tested,Eco-friendly"
                    value={detailForm.tags}
                    onChange={(e) => setDetailForm((f) => ({ ...f, tags: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>Tags — Arabic</label>
                  <input
                    className="oc-input"
                    dir="rtl"
                    placeholder="مُختبر جلدياً,صديق للبيئة"
                    value={detailForm.tags_ar}
                    onChange={(e) => setDetailForm((f) => ({ ...f, tags_ar: e.target.value }))}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Washes Note (English)</label>
                  <input
                    className="oc-input"
                    value={detailForm.washes_note}
                    onChange={(e) =>
                      setDetailForm((f) => ({ ...f, washes_note: e.target.value }))
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Washes Note (Arabic)</label>
                  <input
                    className="oc-input"
                    dir="rtl"
                    value={detailForm.washes_note_ar}
                    onChange={(e) =>
                      setDetailForm((f) => ({ ...f, washes_note_ar: e.target.value }))
                    }
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Manual Wash Min (ml)</label>
                  <input
                    type="number"
                    className="oc-input"
                    value={detailForm.manual_min_ml}
                    onChange={(e) =>
                      setDetailForm((f) => ({ ...f, manual_min_ml: e.target.value }))
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Manual Wash Max (ml)</label>
                  <input
                    type="number"
                    className="oc-input"
                    value={detailForm.manual_max_ml}
                    onChange={(e) =>
                      setDetailForm((f) => ({ ...f, manual_max_ml: e.target.value }))
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Concentration Note (English)</label>
                  <input
                    className="oc-input"
                    value={detailForm.concentration_note}
                    onChange={(e) =>
                      setDetailForm((f) => ({ ...f, concentration_note: e.target.value }))
                    }
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Concentration Note (Arabic)</label>
                  <input
                    className="oc-input"
                    dir="rtl"
                    value={detailForm.concentration_note_ar}
                    onChange={(e) =>
                      setDetailForm((f) => ({ ...f, concentration_note_ar: e.target.value }))
                    }
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group" style={{ flex: 1 }}>
                  <label>How-to-Use Intro (English)</label>
                  <textarea
                    className="oc-input"
                    rows={2}
                    value={detailForm.how_to_use_intro}
                    onChange={(e) =>
                      setDetailForm((f) => ({ ...f, how_to_use_intro: e.target.value }))
                    }
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>How-to-Use Intro (Arabic)</label>
                  <textarea
                    className="oc-input"
                    dir="rtl"
                    rows={2}
                    value={detailForm.how_to_use_intro_ar}
                    onChange={(e) =>
                      setDetailForm((f) => ({ ...f, how_to_use_intro_ar: e.target.value }))
                    }
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Ingredients (English)</label>
                  <textarea
                    className="oc-input"
                    rows={3}
                    value={detailForm.ingredients}
                    onChange={(e) =>
                      setDetailForm((f) => ({ ...f, ingredients: e.target.value }))
                    }
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Ingredients (Arabic)</label>
                  <textarea
                    className="oc-input"
                    dir="rtl"
                    rows={3}
                    value={detailForm.ingredients_ar}
                    onChange={(e) =>
                      setDetailForm((f) => ({ ...f, ingredients_ar: e.target.value }))
                    }
                  />
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button type="submit" className="oc-btn primary" disabled={saveDetail.isPending}>
                  Save Detail
                </button>
              </div>
            </form>
          </div>

          {/* ---------------- Dosage matrix ---------------- */}
          <div className="oc-card" style={{ marginTop: 16 }}>
            <div className="oc-toolbar" style={{ marginBottom: 12 }}>
              <div>
                <strong>How-to-Use Dosage Matrix (ml)</strong>
                <div style={{ fontSize: 12, color: "#777" }}>
                  Soil level × load size — drives the website dose calculator.
                </div>
              </div>
            </div>
            <table className="oc-table" style={{ maxWidth: 480 }}>
              <thead>
                <tr>
                  <th>Soil Level</th>
                  {LOAD_SIZES.map((l) => (
                    <th key={l} style={{ width: 140, textTransform: "capitalize" }}>
                      {l} load
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {SOIL_LEVELS.map((soil) => (
                  <tr key={soil}>
                    <td style={{ textTransform: "capitalize" }}>{soil}</td>
                    {LOAD_SIZES.map((load) => (
                      <td key={load}>
                        <input
                          type="number"
                          className="oc-input"
                          value={dosageGrid[`${soil}|${load}`] ?? ""}
                          onChange={(e) =>
                            setDosageGrid((g) => ({
                              ...g,
                              [`${soil}|${load}`]: e.target.value
                            }))
                          }
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
              <button className="oc-btn primary" disabled={saveDosage.isPending} onClick={handleSaveDosage}>
                Save Dosage
              </button>
            </div>
          </div>

          {/* ---------------- Features ---------------- */}
          <div className="oc-card" style={{ marginTop: 16 }}>
            <div className="oc-toolbar" style={{ marginBottom: 12 }}>
              <strong>Feature Tiles</strong>
            </div>
            <table className="oc-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Subtitle</th>
                  <th>Image</th>
                  <th style={{ width: 60 }}>Sort</th>
                  <th style={{ width: 150 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {(content?.features || []).length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ textAlign: "center", padding: 16 }}>
                      No feature tiles
                    </td>
                  </tr>
                )}
                {(content?.features || []).map((ft) => (
                  <tr key={ft.id}>
                    <td>
                      {ft.title}
                      {ft.title_ar ? (
                        <div dir="rtl" style={{ fontSize: 12, color: "#2563eb", textAlign: "left" }}>
                          {ft.title_ar}
                        </div>
                      ) : null}
                    </td>
                    <td>
                      {ft.subtitle}
                      {ft.subtitle_ar ? (
                        <div dir="rtl" style={{ fontSize: 12, color: "#2563eb", textAlign: "left" }}>
                          {ft.subtitle_ar}
                        </div>
                      ) : null}
                    </td>
                    <td style={{ fontSize: 12, color: "#777" }}>{ft.image}</td>
                    <td>{ft.sort_order}</td>
                    <td>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          className="oc-btn blue"
                          onClick={() => {
                            setFeatureEditId(ft.id);
                            setFeatureForm({
                              title: ft.title || "",
                              title_ar: ft.title_ar || "",
                              subtitle: ft.subtitle || "",
                              subtitle_ar: ft.subtitle_ar || "",
                              image: ft.image || "",
                              sort_order: ft.sort_order ?? 0
                            });
                          }}
                        >
                          edit
                        </button>
                        <button
                          className="oc-btn danger"
                          onClick={() => del(deleteFeature, ft.id, "feature tile")}
                        >
                          delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <form onSubmit={handleSaveFeature} style={{ marginTop: 12 }}>
              <div className="form-row">
                <div className="form-group">
                  <label>{featureEditId ? `Edit Tile #${featureEditId} — Title (English)` : "New Tile — Title (English)"}</label>
                  <input
                    className="oc-input"
                    value={featureForm.title}
                    onChange={(e) => setFeatureForm((f) => ({ ...f, title: e.target.value }))}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Title (Arabic)</label>
                  <input
                    className="oc-input"
                    dir="rtl"
                    value={featureForm.title_ar}
                    onChange={(e) => setFeatureForm((f) => ({ ...f, title_ar: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>Image path</label>
                  <input
                    className="oc-input"
                    value={featureForm.image}
                    onChange={(e) => setFeatureForm((f) => ({ ...f, image: e.target.value }))}
                  />
                </div>
                <div className="form-group" style={{ maxWidth: 90 }}>
                  <label>Sort</label>
                  <input
                    type="number"
                    className="oc-input"
                    value={featureForm.sort_order}
                    onChange={(e) =>
                      setFeatureForm((f) => ({ ...f, sort_order: e.target.value }))
                    }
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Subtitle (English)</label>
                  <input
                    className="oc-input"
                    value={featureForm.subtitle}
                    onChange={(e) => setFeatureForm((f) => ({ ...f, subtitle: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>Subtitle (Arabic)</label>
                  <input
                    className="oc-input"
                    dir="rtl"
                    value={featureForm.subtitle_ar}
                    onChange={(e) => setFeatureForm((f) => ({ ...f, subtitle_ar: e.target.value }))}
                  />
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                {featureEditId && (
                  <button
                    type="button"
                    className="oc-btn ghost"
                    onClick={() => {
                      setFeatureEditId(null);
                      setFeatureForm(emptyFeature);
                    }}
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  className="oc-btn primary"
                  disabled={createFeature.isPending || saveFeature.isPending}
                >
                  {featureEditId ? "Save Tile" : "Add Tile"}
                </button>
              </div>
            </form>
          </div>

          {/* ---------------- Reviews ---------------- */}
          <div className="oc-card" style={{ marginTop: 16 }}>
            <div className="oc-toolbar" style={{ marginBottom: 12 }}>
              <div>
                <strong>Reviews</strong>
                <div style={{ fontSize: 12, color: "#777" }}>
                  The website rating breakdown is derived from these rows.
                </div>
              </div>
            </div>
            <table className="oc-table">
              <thead>
                <tr>
                  <th style={{ width: 140 }}>Author</th>
                  <th style={{ width: 70 }}>Rating</th>
                  <th>Body</th>
                  <th style={{ width: 90 }}>Source</th>
                  <th style={{ width: 80 }}>Verified</th>
                  <th style={{ width: 110 }}>Date</th>
                  <th style={{ width: 150 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {(content?.reviews || []).length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ textAlign: "center", padding: 16 }}>
                      No reviews
                    </td>
                  </tr>
                )}
                {(content?.reviews || []).map((r) => (
                  <tr key={r.id}>
                    <td>{r.author}</td>
                    <td>{r.rating}★</td>
                    <td style={{ fontSize: 13 }}>{r.body}</td>
                    <td>{r.source}</td>
                    <td>{r.verified ? "yes" : "no"}</td>
                    <td>{r.date_added ? String(r.date_added).substring(0, 10) : ""}</td>
                    <td>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          className="oc-btn blue"
                          onClick={() => {
                            setReviewEditId(r.id);
                            setReviewForm({
                              author: r.author || "",
                              rating: r.rating ?? 5,
                              body: r.body || "",
                              source: r.source || "",
                              verified: !!r.verified
                            });
                          }}
                        >
                          edit
                        </button>
                        <button
                          className="oc-btn danger"
                          onClick={() => del(deleteReview, r.id, "review")}
                        >
                          delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <form onSubmit={handleSaveReview} style={{ marginTop: 12 }}>
              <div className="form-row">
                <div className="form-group">
                  <label>{reviewEditId ? `Edit Review #${reviewEditId} — Author` : "New Review — Author"}</label>
                  <input
                    className="oc-input"
                    value={reviewForm.author}
                    onChange={(e) => setReviewForm((f) => ({ ...f, author: e.target.value }))}
                    required
                  />
                </div>
                <div className="form-group" style={{ maxWidth: 110 }}>
                  <label>Rating</label>
                  <select
                    className="oc-input"
                    value={reviewForm.rating}
                    onChange={(e) => setReviewForm((f) => ({ ...f, rating: e.target.value }))}
                  >
                    {[5, 4, 3, 2, 1].map((n) => (
                      <option key={n} value={n}>
                        {n}★
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ maxWidth: 140 }}>
                  <label>Source</label>
                  <input
                    className="oc-input"
                    placeholder="google / site"
                    value={reviewForm.source}
                    onChange={(e) => setReviewForm((f) => ({ ...f, source: e.target.value }))}
                  />
                </div>
                <div className="form-group" style={{ maxWidth: 110 }}>
                  <label>Verified</label>
                  <div style={{ marginTop: 8 }}>
                    <input
                      type="checkbox"
                      checked={reviewForm.verified}
                      onChange={(e) =>
                        setReviewForm((f) => ({ ...f, verified: e.target.checked }))
                      }
                    />
                  </div>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Body</label>
                  <textarea
                    className="oc-input"
                    rows={2}
                    value={reviewForm.body}
                    onChange={(e) => setReviewForm((f) => ({ ...f, body: e.target.value }))}
                  />
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                {reviewEditId && (
                  <button
                    type="button"
                    className="oc-btn ghost"
                    onClick={() => {
                      setReviewEditId(null);
                      setReviewForm(emptyReview);
                    }}
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  className="oc-btn primary"
                  disabled={createReview.isPending || saveReview.isPending}
                >
                  {reviewEditId ? "Save Review" : "Add Review"}
                </button>
              </div>
            </form>
          </div>

          {/* ---------------- Q&A ---------------- */}
          <div className="oc-card" style={{ marginTop: 16, marginBottom: 16 }}>
            <div className="oc-toolbar" style={{ marginBottom: 12 }}>
              <strong>Questions & Answers</strong>
            </div>
            <table className="oc-table">
              <thead>
                <tr>
                  <th>Question</th>
                  <th>Answer</th>
                  <th style={{ width: 60 }}>Sort</th>
                  <th style={{ width: 150 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {(content?.qa || []).length === 0 && (
                  <tr>
                    <td colSpan={4} style={{ textAlign: "center", padding: 16 }}>
                      No Q&A
                    </td>
                  </tr>
                )}
                {(content?.qa || []).map((q) => (
                  <tr key={q.id}>
                    <td>
                      {q.question}
                      {q.question_ar ? (
                        <div dir="rtl" style={{ fontSize: 12, color: "#2563eb", textAlign: "left" }}>
                          {q.question_ar}
                        </div>
                      ) : null}
                    </td>
                    <td style={{ fontSize: 13 }}>
                      {q.answer}
                      {q.answer_ar ? (
                        <div dir="rtl" style={{ fontSize: 12, color: "#2563eb", textAlign: "left" }}>
                          {q.answer_ar}
                        </div>
                      ) : null}
                    </td>
                    <td>{q.sort_order}</td>
                    <td>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          className="oc-btn blue"
                          onClick={() => {
                            setQaEditId(q.id);
                            setQaForm({
                              question: q.question || "",
                              question_ar: q.question_ar || "",
                              answer: q.answer || "",
                              answer_ar: q.answer_ar || "",
                              sort_order: q.sort_order ?? 0
                            });
                          }}
                        >
                          edit
                        </button>
                        <button className="oc-btn danger" onClick={() => del(deleteQa, q.id, "Q&A")}>
                          delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <form onSubmit={handleSaveQa} style={{ marginTop: 12 }}>
              <div className="form-row">
                <div className="form-group" style={{ flex: 1 }}>
                  <label>{qaEditId ? `Edit Q&A #${qaEditId} — Question (English)` : "New Q&A — Question (English)"}</label>
                  <input
                    className="oc-input"
                    value={qaForm.question}
                    onChange={(e) => setQaForm((f) => ({ ...f, question: e.target.value }))}
                    required
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Question (Arabic)</label>
                  <input
                    className="oc-input"
                    dir="rtl"
                    value={qaForm.question_ar}
                    onChange={(e) => setQaForm((f) => ({ ...f, question_ar: e.target.value }))}
                  />
                </div>
                <div className="form-group" style={{ maxWidth: 90 }}>
                  <label>Sort</label>
                  <input
                    type="number"
                    className="oc-input"
                    value={qaForm.sort_order}
                    onChange={(e) => setQaForm((f) => ({ ...f, sort_order: e.target.value }))}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Answer (English)</label>
                  <textarea
                    className="oc-input"
                    rows={2}
                    value={qaForm.answer}
                    onChange={(e) => setQaForm((f) => ({ ...f, answer: e.target.value }))}
                    required
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Answer (Arabic)</label>
                  <textarea
                    className="oc-input"
                    dir="rtl"
                    rows={2}
                    value={qaForm.answer_ar}
                    onChange={(e) => setQaForm((f) => ({ ...f, answer_ar: e.target.value }))}
                  />
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                {qaEditId && (
                  <button
                    type="button"
                    className="oc-btn ghost"
                    onClick={() => {
                      setQaEditId(null);
                      setQaForm(emptyQa);
                    }}
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  className="oc-btn primary"
                  disabled={createQa.isPending || saveQa.isPending}
                >
                  {qaEditId ? "Save Q&A" : "Add Q&A"}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
}
